import React, { useEffect, useMemo, useState } from "react";
import { FaSpinner } from "react-icons/fa";

const formatElapsed = (totalSeconds) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const formatStageLabel = (stage) => {
  if (!stage || typeof stage !== "string") return null;
  if (stage === "phase1") return "Stage 1";
  if (stage === "phase2") return "Stage 2";
  return stage;
};

const IndeterminateBar = () => (
  <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-blue-100">
    <div className="h-full w-1/2 animate-[indicator_1.2s_ease-in-out_infinite] rounded-full bg-blue-500/80" />
  </div>
);

const LongRunningTaskIndicator = ({
  visible,
  title = "Working...",
  phase,
  reassurance,
  elapsedSeconds = 0,
  startedAt = null,
  onCancel,
  requestId,
  solverProgress = null,
  scopeChips = [],
  metaChips = [],
}) => {
  const [detailsCollapsed, setDetailsCollapsed] = useState(true);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!visible) return undefined;
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [visible]);

  useEffect(() => {
    setDetailsCollapsed(true);
  }, [requestId]);

  const hasSolverProgress = Boolean(solverProgress && typeof solverProgress === "object");
  const hasDetails = Boolean(hasSolverProgress || requestId);
  const showTechnicalDetails = process.env.NODE_ENV === "development" && hasDetails;
  const solverName = hasSolverProgress ? (solverProgress.solver || "Solver") : null;
  const currentGap = hasSolverProgress ? solverProgress.current_mip_gap : null;
  const targetGap = hasSolverProgress ? solverProgress.target_mip_gap : null;
  const bestObjective = hasSolverProgress ? solverProgress.best_objective : null;
  const bestBound = hasSolverProgress ? solverProgress.best_bound : null;
  const nodeCount = hasSolverProgress ? solverProgress.node_count : null;
  const hasIncumbent = hasSolverProgress ? Boolean(solverProgress.has_incumbent) : false;
  const currentStage = hasSolverProgress ? formatStageLabel(solverProgress.stage) : null;
  const solverTimeLimitSeconds = hasSolverProgress ? solverProgress.time_limit_seconds : null;
  const computedElapsedSeconds = useMemo(() => {
    if (!startedAt) {
      return Math.max(0, elapsedSeconds || 0);
    }
    const startedAtMs = new Date(startedAt).getTime();
    if (Number.isNaN(startedAtMs)) {
      return Math.max(0, elapsedSeconds || 0);
    }
    return Math.max(0, Math.floor((nowMs - startedAtMs) / 1000));
  }, [elapsedSeconds, nowMs, startedAt]);

  const formatPercent = (value) => (typeof value === "number" ? `${(value * 100).toFixed(2)}%` : "—");
  const formatNumber = (value) =>
    typeof value === "number"
      ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : "—";

  if (!visible) return null;

  return (
    <div
      className="rounded-xl border border-blue-200 bg-gradient-to-br from-white to-blue-50 p-5 font-ui shadow-2xl ring-1 ring-blue-100 motion-safe:animate-[softPop_220ms_ease-out]"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <style>{`
        @keyframes indicator {
          0% { transform: translateX(-110%); }
          100% { transform: translateX(220%); }
        }
        @keyframes softPop {
          0% { transform: translateY(8px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="rounded-full bg-blue-100 p-2">
            <FaSpinner className="animate-spin text-blue-700" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-base font-extrabold text-gray-900">{title}</div>
            {phase && <div className="mt-1 text-sm text-gray-700">{phase}</div>}
          </div>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="shrink-0 rounded-md border border-red-200 bg-white px-2.5 py-1.5 text-xs font-extrabold text-red-700 hover:bg-red-50"
          >
            Cancel
          </button>
        )}
      </div>

      <IndeterminateBar />

      {scopeChips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
          {scopeChips.map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-slate-200 bg-white px-2 py-1 font-semibold text-slate-700"
            >
              {chip}
            </span>
          ))}
        </div>
      )}

      {metaChips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
          {metaChips.map((chip) => (
            <span key={chip} className="rounded-full border border-blue-100 bg-blue-50 px-2 py-1 font-semibold text-blue-700">
              {chip}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-xs font-semibold text-gray-600">
        <div className="tabular-nums text-gray-800">{formatElapsed(computedElapsedSeconds)}</div>
        {showTechnicalDetails && (
          <button
            type="button"
            onClick={() => setDetailsCollapsed((currentValue) => !currentValue)}
            aria-expanded={!detailsCollapsed}
            className="rounded-md border border-blue-200 bg-white px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-50"
          >
            {detailsCollapsed ? "Technical details" : "Hide technical details"}
          </button>
        )}
      </div>
      {!detailsCollapsed && showTechnicalDetails && (
        <div className="mt-3">
          {hasSolverProgress && (
            <div className="rounded-md border border-blue-100 bg-white/70 p-2 text-[11px] text-slate-700">
              <div className="font-semibold text-slate-800">{solverName} progress</div>
              {currentStage && <div className="mt-1">Current stage: {currentStage}</div>}
              <div>Time limit: {solverTimeLimitSeconds == null ? "None" : formatElapsed(solverTimeLimitSeconds)}</div>
              <div className="mt-1">Current MIP gap: {currentGap == null ? "MIP gap unavailable yet" : formatPercent(currentGap)}</div>
              <div>Target MIP gap: {formatPercent(targetGap)}</div>
              <div>Best objective: {hasIncumbent ? formatNumber(bestObjective) : "Finding first feasible solution..."}</div>
              <div>Best bound: {formatNumber(bestBound)}</div>
              <div>Nodes: {formatNumber(nodeCount)}</div>
            </div>
          )}
          {requestId && (
            <div className="mt-2 text-[11px] text-gray-500">
              <span className="font-semibold text-gray-700">Request ID</span>:{" "}
              <span className="font-mono">{requestId}</span>
            </div>
          )}
        </div>
      )}
      {reassurance && <div className="mt-3 text-[11px] text-slate-500">{reassurance}</div>}
    </div>
  );
};

export default LongRunningTaskIndicator;
